import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface SummaryCardPdfProps {
  content: string;
  imageUrl?: string;
  customizationSettings: PdfCustomizationSettings;
  author: string;
  createdAt: string;
  title?: string; // Added optional title prop
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#f0fdfa", // teal-50
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
    backgroundColor: "#ccfbf1", // teal-100
    padding: 8,
    borderRadius: 6,
  },
  title: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#0d9488", // teal-600
  },
  content: {
    ...commonStyles.sectionContent,
    // backgroundColor: "#ffffff",
    padding: 10,
    borderRadius: 6,
    marginTop: 8,
  },
});

const SummaryDocument = ({
  content,
  imageUrl = "",
  author,
  createdAt,
  customizationSettings,
  title,
}: SummaryCardPdfProps) => {
  const { t } = useTranslation();

  // Extract title from content if not provided explicitly
  const documentTitle =
    title || content.slice(0, 50).replace(/[#*]/g, "").trim();

  return (
    <PdfLayout
      imageUrl={imageUrl}
      headerTextColor="#0d9488"
      customizationSettings={customizationSettings}
      author={author}
      createdDate={createdAt}
      title={documentTitle}
      topic={documentTitle}
      courseName="" // Empty as summaries don't have course names
      gradeLevel={0} // Default grade level
      subject="" // Empty as summaries don't have subjects
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <Text
            style={[
              styles.title,
              {
                fontSize: scaleFontSize(
                  styles.title.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            ]}
          >
            {t("summaries.details")}
          </Text>
        </View>
        <MarkdownRenderer
          customStyle={{
            h3: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },
            blockquote: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },
            listItem: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },
            h2: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },
            text: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },
            h1: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },
          }}
          content={content}
        />
      </View>
    </PdfLayout>
  );
};

function SummaryCardPDF(props: SummaryCardPdfProps) {
  return <SummaryDocument {...props} />;
}

export default SummaryCardPDF;
